import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  AdvancedFilterButton,
  AdvancedFilterPanel,
  FilterPanelCollapse,
} from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { useRevealLastViewedRow } from '@/hooks/useLastViewedRow';
import { getFilterPanelProps } from '@/hooks/useUrlSyncedFilters';
import { teacherInactivationListInfiniteQueryOptions } from '../api/teacherInactivations.api';
import {
  getTeacherInactivationColumns,
  TEACHER_COLUMN_ID,
} from '../components/TeacherInactivationColumns';
import { TeacherInactivationSummaryCards } from '../components/TeacherInactivationSummaryCards';
import { TEACHER_INACTIVATION_SEARCH_PLACEHOLDER } from '../constants/teacherInactivation.constants';
import { useTeacherInactivationFilterFields } from '../hooks/useTeacherInactivationFilterFields';
import { useTeacherInactivationFilters } from '../hooks/useTeacherInactivationFilters';
import type { TeacherInactivationRequest } from '../types/teacherInactivation.types';

export function TeacherInactivationListPage() {
  const navigate = useNavigate();
  const filters = useTeacherInactivationFilters();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { fields, hasOptionsError } = useTeacherInactivationFilterFields({
    isEnabled: isFilterOpen,
    filterValues: filters.filterValues,
  });

  const { rows, isLoading, loadError, refetch, loadMoreProps } = useInfiniteTableQuery(
    teacherInactivationListInfiniteQueryOptions(filters.listParams),
  );
  const columns = useMemo(
    () => getTeacherInactivationColumns(filters.listParams.search),
    [filters.listParams.search],
  );
  const lastViewedRequestId = useRevealLastViewedRow('teacherInactivations');

  function handleRowClick(request: TeacherInactivationRequest) {
    void navigate({
      to: '/settings/teacher-inactivations/$requestId',
      params: { requestId: request.id },
    });
  }

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="Teacher Inactivation"
        titleAddon={
          <DateRangeBadge
            onClick={() => setIsFilterOpen(true)}
            dateFrom={filters.dateFrom}
            dateTo={filters.dateTo}
          />
        }
        search={{
          value: filters.searchText,
          onChange: filters.setSearchText,
          placeholder: TEACHER_INACTIVATION_SEARCH_PLACEHOLDER,
        }}
        actions={
          <AdvancedFilterButton
            isOpen={isFilterOpen}
            activeCount={filters.activeFilterCount}
            onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
          />
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <div className="flex flex-col gap-2">
          <AdvancedFilterPanel fields={fields} {...getFilterPanelProps(filters)} />
          {hasOptionsError && (
            <p className="text-xs text-destructive">
              Some filter options could not be loaded. Refresh the page to try again.
            </p>
          )}
        </div>
      </FilterPanelCollapse>

      <TeacherInactivationSummaryCards params={filters.summaryParams} />

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the teacher inactivation requests"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          stickyColumnId={TEACHER_COLUMN_ID}
          className="lg:flex-1"
          emptyMessage="No inactivation requests"
          emptyHint="Change the search or reset the filters to see more."
          isRowHighlighted={(request) => request.id === lastViewedRequestId}
          onRowClick={handleRowClick}
          {...loadMoreProps}
        />
      )}
    </div>
  );
}
